import { attachEvent } from '../../js/tools';
import fEvents from '../../js/events';
import '../tooltip/tooltip';
import '../modalbox/modalbox';
import Modalbox from '../modalbox/modalbox';

export enum ConnectionState {
    CONNECTING = 'CONNECTING',
    CONNECTED = 'CONNECTED',
    DISCONNECTED = 'DISCONNECTED',
    RECONNECTING = 'RECONNECTING',
    SESSION_ENDED = 'SESSION_ENDED',
    SESSION_CLOSED = 'SESSION_CLOSED',
}

enum RealTimeEventTypes {
    BROWSER_STREAM = 'browser_stream',
    BROWSER_ACTIVITY = 'browser_activity',
}

export interface BrowserStreamData {
    image: string;
}

/**
 * A custom element that manages a streaming connection.
 *  It should handle everything related to the connection, including reconnection attempts, heartbeats, and session management.
 *  show the connection state in the UI and you should be able to reconnect here 
 */
export class BrowserHeader extends HTMLElement {
    private csrfToken: string;
    private heartbeatTimeout = 10000; // 10 seconds
    private heartbeatTimer: any;
    private state: ConnectionState = ConnectionState.CONNECTING;
    private reconnectAttempts = 0;
    private maxReconnectAttempts = 1;

    constructor() {
        super();
        this.heartbeatTimeout = parseInt(this.getAttribute('heartbeat-timeout') || '10000', 10);
        this.attachShadow({ mode: 'open' });
        this.render();
    }

    connectedCallback() {

        attachEvent('#reconnect-btn', 'click', () => {
            // Attempt to reconnect by restarting the connection manager.
            this.endSession();
        }, this.shadowRoot);

        attachEvent('.action--end-session', 'click', this.onEndSession, this.shadowRoot);
    }

    /**
   * Starts the connection process using the provided CSRF token.
   * @param csrfToken The token used to authenticate the stream connection.
   */
    public connect(csrfToken: string, heartbeatTimeout = 10000) {
        this.csrfToken = csrfToken;
        this.heartbeatTimeout = heartbeatTimeout;
        this.setState(ConnectionState.CONNECTING);
        this.listenStream();
        this.resetHeartbeat();
    }

    public reconnectBrowser() {
        this.setState(ConnectionState.CONNECTING);
        this.resetHeartbeat();
    }

    /**
   * Registers a listener for realtime stream data.
   */
    private listenStream() {
        fEvents.listenRealtimeServer(this.csrfToken, this.handleStreamData);
    }

    /**
   * Called when a stream event is received. It parses the incoming data,
   * updates the connection state, and dispatches a 'data' event.
   */
    private handleStreamData = (e: any) => {
        try {
            const data = JSON.parse(e.data);
            if (data.eventType === RealTimeEventTypes.BROWSER_ACTIVITY) {
                this.resetHeartbeat();
                const acitivity = data.data.activity;
                console.log('activity received:', acitivity);
                if (acitivity.loading) {
                    this.setState(ConnectionState.CONNECTING);
                } else if (acitivity.success) {
                    this.setState(ConnectionState.CONNECTED);
                } else if (!acitivity.success && !acitivity.loading) {
                    clearTimeout(this.heartbeatTimer);
                    this.setState(ConnectionState.SESSION_ENDED);
                }
                // Handle browser activity events if needed
                console.log('Browser activity event received:', acitivity);
                return;
            } else if (data.eventType === RealTimeEventTypes.BROWSER_STREAM) {
                this.resetHeartbeat();

                if (this.state !== ConnectionState.CONNECTED) {
                    this.setState(ConnectionState.CONNECTED);
                }
                fEvents.dispatch(this, 'data', data.data);
            }
        } catch (error) {
            console.error('Error processing stream data:', error);
        }
    };

    /**
   * Clears any existing heartbeat timer and starts a new one.
   */
    private resetHeartbeat() {
        if (this.heartbeatTimer) {
            clearTimeout(this.heartbeatTimer);
        }
        this.heartbeatTimer = setTimeout(() => {
            this.handleDisconnect();
        }, this.heartbeatTimeout);
    }

    /**
   * Called when no heartbeat is received within the timeout period.
   */
    private handleDisconnect() {
        this.setState(ConnectionState.DISCONNECTED);
        this.tryReconnect();
    }

    /**
   * Attempts to reconnect if the maximum number of reconnection attempts has not been exceeded.
   */
    private tryReconnect() {
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
            this.setState(ConnectionState.RECONNECTING);
            // Wait 3 seconds before trying to reconnect.
            setTimeout(() => {
                this.reconnectAttempts++;
                this.connect(this.csrfToken);
            }, 3000);
        } else {
            this.setState(ConnectionState.SESSION_ENDED);
        }
    }

    /**
   * Updates the connection state and dispatches a 'stateChange' event.
   * @param newState The new connection state.
   */
    private setState(newState: ConnectionState) {
        this.state = newState;
        console.log('state changed to:', newState);
        fEvents.dispatch(this, 'stateChange', { state: newState });

        this.renderLoader(newState);
    }

    /**
   * Forcefully ends the connection session, stops the realtime stream, and clears any timers.
   */
    public endSession() {
        this.setState(ConnectionState.SESSION_CLOSED);
        fEvents.stopListeningRealtime(this.csrfToken, this.handleStreamData);
        if (this.heartbeatTimer) {
            clearTimeout(this.heartbeatTimer);
        }
    }

    private onEndSession = () => {
        const modal = this.shadowRoot.querySelector('#confirm-modal') as Modalbox;

        void modal
            .confirm('Are you sure you want to end the session? This will close the browser.')
            .then(confirmed => {
                if (!confirmed) {
                    return;
                }
                this.endSession();
            });
    };

    get elements() {
        return {
            connectionStatus: this.shadowRoot.querySelector('.connection-status') as HTMLElement,
        };
    }

    private renderLoader = state => {
        this.elements.connectionStatus.style.display = 'flex';
        switch (state) {
            case ConnectionState.CONNECTING:
                this.elements.connectionStatus.innerHTML = `
                    <span class="status-text">
                        <i class="material-icons status-icon is-rotating">sync</i>
                        <span>Connecting to browser...</span>
                    </span>
                `;
                break;
            case ConnectionState.CONNECTED:
                this.elements.connectionStatus.style.display = 'none';
                break;
            case ConnectionState.DISCONNECTED:
                this.elements.connectionStatus.innerHTML = `
                    <span class="status-text">
                        <i class="material-icons status-icon">cancel</i>
                        <span>Connection lost</span>
                    </span>
                `;
                break;
            case ConnectionState.RECONNECTING:
                this.elements.connectionStatus.innerHTML = `
                    <span class="status-text">
                        <i class="material-icons status-icon is-rotating">sync</i>
                       <span>Reconnecting...</span>
                    </span>
                `;
                break;
            case ConnectionState.SESSION_ENDED: {
                this.elements.connectionStatus.innerHTML = `
                    <span class="status-text">
                         <i class="material-icons status-icon">cloud_off</i>
                         <span>Connection lost with browser</span>
                    </span>
                    <div class="flex-row">
                        <button id="reconnect-btn" class="browser-action-btn restart-btn">
                            <i class="material-icons">sync</i>
                            Restart
                        </button>
                    </div>
                `;
            }
                break;
            case ConnectionState.SESSION_CLOSED:
                this.elements.connectionStatus.innerHTML = `
                    <span class="status-text">
                         <i class="material-icons status-icon">block</i>
                         Session closed
                    </span>
                `;
                break;
        }

    };

    private render() {
        this.shadowRoot.innerHTML = `
            <link rel="stylesheet" href="https://fonts.googleapis.com/icon?family=Material+Icons">
            <link rel="stylesheet" href="/styles/index.css">
            <style>
               .browser-action-btn { display: flex; align-items: center; border-radius: 4px; border: none; padding: 4px 8px; cursor: pointer; gap: 4px; font-size: 12px; }
               .end-btn { background: #b12e2e; color: white; }
               .restart-btn { background: #2a812a; color: white; }
               .flex-row { display: flex; gap: 8px; }
               .browser-header-actions { display: flex; align-items: center; gap: 12px; position: relative; }
               .connection-status { display: flex; align-items: center; gap: 12px; position: absolute; z-index: 9999; width: 305px; right: 0; padding: 8px; border-radius: 8px; top: 43px; background: white; box-shadow: 1px 1px 3px 2px #6b676726;}
               .status-text { font-weight: bold; font-size: 12px; display: flex; align-items: center; gap: 12px; }
            </style>

             <div class="browser-header-actions">
                    <div class="connection-status"></div>

                    <button class="browser-action-btn browser-action--close action--end-session end-btn">
                        <i class="material-icons">crop_square</i>
                        End session
                    </button>
            </div>

            <fl-tooltip for=".browser-action--close">
                This will end the browser session and close the browser.
            </fl-tooltip>

          <modal-box id="confirm-modal"></modal-box>
        `;
    }
}

customElements.define('browser-header', BrowserHeader);
